import { Button, ButtonStyles, DefaultFooterComponent, Icons, Popup, Upload } from '@silverfort/sf-components';
import { FC, useCallback, useEffect, useState } from 'react';
import { useApplicationsContext } from './context/ApplicationsContext';
import { useUpdateAppsFromCsv } from '../../../api/mutations/useUpdateAppsFromCsv';
import { useGetUploadApplicationsFormat } from '../../../api/queries/useGetUploadApplicationsFormat';

interface PopupContentProps {
    downloadFormat: () => void;
    fileData: FormData | null;
    onFileSelected: (files: FileList | Array<File | null> | null) => void;
}

const PopupContent: FC<PopupContentProps> = ({ downloadFormat, fileData, onFileSelected }) => {
    return (
        <div className="flex flex-col flex-1 gap-4">
            <div className="border border-sf-gray-100 p-6 rounded-lg flex flex-col gap-2 h-fit">
                <span>To allow complete compatibility with our system please use the attached template.</span>

                <Button
                    text="Download Template CSV"
                    iconLeft={Icons.Download}
                    onClick={downloadFormat}
                    buttonStyle={ButtonStyles.Primary}
                />
            </div>

            <div className="border border-sf-gray-100 p-6 rounded-lg flex flex-col gap-2 h-full">
                <Upload
                    completed={!!fileData}
                    className="w-full items-center align-middle h-full"
                    onChange={(file) => onFileSelected(file)}
                    subtitle="Max file size up to 10 MB"
                    onRemove={() => onFileSelected(null)}
                    accept=".csv"
                />
            </div>
        </div>
    );
};

export const UploadApplicationsModal: FC = () => {
    const { showUploadApplicationsModal, setShowUploadApplicationsModal } = useApplicationsContext();
    const { downloadFormat } = useGetUploadApplicationsFormat();
    const { updateAppsFromCsvMutation } = useUpdateAppsFromCsv();

    const [fileData, setFileData] = useState<FormData | null>(null);

    const close = useCallback(() => {
        setShowUploadApplicationsModal(false);
    }, [setShowUploadApplicationsModal]);

    const upload = useCallback(() => {
        updateAppsFromCsvMutation(fileData);
    }, [updateAppsFromCsvMutation, fileData]);

    const onFileSelected = (files: FileList | Array<File | null> | null) => {
        if (files && files[0]) {
            const file = files[0];
            const data = new FormData();

            data.append('file', file);

            setFileData(data);
        } else {
            setFileData(null);
        }
    };

    useEffect(() => {
        if (!showUploadApplicationsModal) {
            setFileData(null);
        }
    }, [showUploadApplicationsModal]);

    return (
        <Popup
            ContentComponent={PopupContent}
            ContentComponentProps={{ onFileSelected, fileData, downloadFormat }}
            FooterComponent={DefaultFooterComponent}
            FooterComponentProps={{
                primaryButtonProps: {
                    disabled: fileData === null,
                    text: 'Upload'
                }
            }}
            onApprove={upload}
            onClose={close}
            open={showUploadApplicationsModal}
            popupClassName="!w-[680px] !h-[520px]"
            title="Upload from CSV"
        />
    );
};
